import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import CouponForm from '../components/CouponForm';
import Toast from '../components/Toast';

function AdminDashboard() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCoupons();
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllCoupons();
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        showToast('Failed to load coupons', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleAddCoupon = () => {
    setEditingCoupon(null);
    setShowModal(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setShowModal(true);
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      await adminApi.deleteCoupon(id);
      showToast('Coupon deleted successfully', 'success');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      showToast('Failed to delete coupon', 'error');
    }
  };

  const handleSaveCoupon = async (couponData) => {
    try {
      if (editingCoupon) {
        await adminApi.updateCoupon(editingCoupon.id, couponData);
        showToast('Coupon updated successfully', 'success');
      } else {
        await adminApi.createCoupon(couponData);
        showToast('Coupon created successfully', 'success');
      }
      setShowModal(false);
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      showToast('Failed to save coupon', 'error');
      throw error;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)' }}>
      {toast && (
        <div className="toast-container">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </div>
      )}

      <div className="header">
        <div className="container">
          <div className="admin-header">
            <h1>🛠️ Coupon Adda - Admin Dashboard</h1>
            <button onClick={handleLogout}>🚪 Logout</button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="admin-actions">
          <button onClick={handleAddCoupon}>➕ Add New Coupon</button>
        </div>

        {loading ? (
          <div className="loading">Loading coupons...</div>
        ) : coupons.length === 0 ? (
          <div className="empty-state">
            <h2>No coupons yet</h2>
            <p>Add your first coupon to get started!</p>
          </div>
        ) : (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Brand</th>
                  <th>App</th>
                  <th>Category</th>
                  <th>Code</th>
                  <th>Expiry</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td>{coupon.title}</td>
                    <td>{coupon.brandName}</td>
                    <td>{coupon.appName}</td>
                    <td>{coupon.category}</td>
                    <td>{coupon.couponCode || 'N/A'}</td>
                    <td>{formatDate(coupon.expiryDate)}</td>
                    <td>
                      <button 
                        className="btn-primary" 
                        onClick={() => handleEditCoupon(coupon)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-danger" 
                        onClick={() => handleDeleteCoupon(coupon.id)}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <CouponForm
            coupon={editingCoupon}
            onSave={handleSaveCoupon}
            onClose={() => setShowModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
